import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, CheckCircle, XCircle, Clock, AlertCircle, FileText, Image as ImageIcon,
  User, Phone, MapPin, Calendar, Flag, Search, Filter, X, Download, Loader2, Eye,
  ClipboardList, ArrowUpCircle, RefreshCw, Users
} from 'lucide-react';
import { kycAPI } from '../services/api';

function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

function LevelBadge({ level }) {
  const config = {
    basic:    { bg: 'color-mix(in srgb, var(--accent-success) 15%, transparent)', color: 'var(--accent-success)',  label: 'Basic'    },
    moderate: { bg: 'color-mix(in srgb, var(--accent-info)    15%, transparent)', color: 'var(--accent-info)',     label: 'Moderate' },
    verified: { bg: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)',  label: 'Verified' },
  }[level] || { bg: 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)', color: 'var(--text-tertiary)', label: level };

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: config.bg, color: config.color }}>
      <Shield className="w-3 h-3" />{config.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending:     { Icon: Clock,       bg: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)', color: 'var(--accent-warning)', label: 'Pending'     },
    approved:    { Icon: CheckCircle, bg: 'color-mix(in srgb, var(--accent-success) 15%, transparent)', color: 'var(--accent-success)', label: 'Approved'    },
    rejected:    { Icon: XCircle,     bg: 'color-mix(in srgb, var(--accent-danger)  15%, transparent)', color: 'var(--accent-danger)',  label: 'Rejected'    },
    not_started: { Icon: Clock,       bg: 'color-mix(in srgb, var(--text-tertiary)  15%, transparent)', color: 'var(--text-tertiary)',  label: 'Not Started' },
  }[status] || { Icon: Clock, bg: 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)', color: 'var(--text-tertiary)', label: status };

  const { Icon, bg, color, label } = config;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: bg, color }}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
}

function AuditActionBadge({ action }) {
  const config = {
    submitted:         { Icon: ArrowUpCircle, bg: 'color-mix(in srgb, var(--accent-info)    15%, transparent)', color: 'var(--accent-info)',    label: 'Submitted'         },
    approved:          { Icon: CheckCircle,   bg: 'color-mix(in srgb, var(--accent-success) 15%, transparent)', color: 'var(--accent-success)', label: 'Approved'          },
    rejected:          { Icon: XCircle,       bg: 'color-mix(in srgb, var(--accent-danger)  15%, transparent)', color: 'var(--accent-danger)',  label: 'Rejected'          },
    document_uploaded: { Icon: FileText,      bg: 'color-mix(in srgb, var(--accent-warning) 15%, transparent)', color: 'var(--accent-warning)', label: 'Doc Uploaded'      },
    document_approved: { Icon: CheckCircle,   bg: 'color-mix(in srgb, var(--accent-success) 15%, transparent)', color: 'var(--accent-success)', label: 'Doc Approved'      },
    document_rejected: { Icon: XCircle,       bg: 'color-mix(in srgb, var(--accent-danger)  15%, transparent)', color: 'var(--accent-danger)',  label: 'Doc Rejected'      },
    level_upgraded:    { Icon: Shield,        bg: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)', label: 'Level Upgraded'    },
    resubmission:      { Icon: RefreshCw,     bg: 'color-mix(in srgb, var(--accent-info)    15%, transparent)', color: 'var(--accent-info)',    label: 'Resubmission'      },
  }[action] || { Icon: Clock, bg: 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)', color: 'var(--text-tertiary)', label: action };

  const { Icon, bg, color, label } = config;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: bg, color }}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
}

function DocumentViewer({ document, onClose }) {
  if (!document) return null;
  const isImage = document.mime_type?.startsWith('image/');
  const isPdf   = document.mime_type === 'application/pdf';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        style={{ backgroundColor: 'var(--bg-primary)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{document.file_name}</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              {document.document_type?.replace(/_/g, ' ')} • {(document.file_size / 1024).toFixed(0)} KB
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded transition-smooth" style={{ color: 'var(--text-tertiary)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4" style={{ backgroundColor: 'var(--bg-secondary)' }}>
          {isImage && <img src={`/${document.file_path}`} alt={document.file_name} className="max-w-full h-auto rounded-lg shadow-lg mx-auto" />}
          {isPdf   && <iframe src={`/${document.file_path}`} className="w-full h-full min-h-[600px] rounded-lg shadow-lg" title={document.file_name} />}
          {!isImage && !isPdf && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-tertiary)' }} />
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Preview not available</p>
              <a href={`/${document.file_path}`} download className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 text-white rounded-lg text-xs transition-smooth" style={{ backgroundColor: 'var(--accent-primary)' }}>
                <Download className="w-3.5 h-3.5" />Download File
              </a>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-primary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Uploaded {new Date(document.uploaded_at).toLocaleDateString()}
          </p>
          <a href={`/${document.file_path}`} download className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs transition-smooth"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <Download className="w-3.5 h-3.5" />Download
          </a>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ application, onClose, onReview }) {
  const [action, setAction]         = useState(null);
  const [reason, setReason]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [profile, setProfile]       = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    if (!application) return;
    setProfileLoading(true);
    kycAPI.getUserProfile(application.user_id)
      .then(res => setProfile(res.data?.data || null))
      .catch(() => setProfile(null))
      .finally(() => setProfileLoading(false));
  }, [application]);

  const handleSubmit = async () => {
    if (action === 'reject' && !reason.trim()) { alert('Please provide a rejection reason'); return; }
    setSubmitting(true);
    try {
      await onReview(application.user_id, application.pending_level, action, reason);
      onClose();
    } catch (error) {
      alert(error.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (!application) return null;
  const level   = application.pending_level || 'basic';
  const data    = profile?.data || null;
  // Flatten ALL documents across all levels for display
  const allDocs = profile?.documents
    ? Object.entries(profile.documents).flatMap(([lvl, docs]) => docs.map(d => ({ ...d, _level: lvl })))
    : [];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={onClose}>
        <div className="rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
          style={{ backgroundColor: 'var(--bg-primary)' }} onClick={e => e.stopPropagation()}>

          <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Review KYC Application</h2>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                {application.email} • #{application.user_id}
              </p>
            </div>
            <button onClick={onClose} className="p-2 rounded transition-smooth" style={{ color: 'var(--text-tertiary)' }}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6 space-y-6">
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Verification Level</p>
              <LevelBadge level={level} />
            </div>

            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Personal Information</p>
              <div className="rounded-lg p-4 space-y-3" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                {profileLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                ) : data ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { Icon: User,     label: 'Full Name',     value: [data.first_name, data.middle_name, data.last_name].filter(Boolean).join(' ') || '—' },
                        { Icon: Calendar, label: 'Date of Birth', value: data.date_of_birth ? new Date(data.date_of_birth).toLocaleDateString() : '—' },
                        { Icon: Phone,    label: 'Phone',          value: data.phone || '—' },
                        { Icon: Flag,     label: 'Nationality',    value: data.nationality || '—' },
                      ].map(({ Icon, label, value }) => (
                        <div key={label} className="flex items-start gap-2">
                          <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                          <div>
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</p>
                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                      <div>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Address</p>
                        <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                          {[data.address_line1, data.address_line2, data.city, data.state_province, data.postal_code, data.country].filter(Boolean).join(', ') || '—'}
                        </p>
                      </div>
                    </div>
                    {level === 'verified' && data.source_of_funds && (
                      <div className="border-t pt-3 mt-3" style={{ borderColor: 'var(--border-color)' }}>
                        <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Source of Funds</p>
                        <p className="text-sm font-medium capitalize" style={{ color: 'var(--text-primary)' }}>
                          {data.source_of_funds.replace(/_/g, ' ')}
                        </p>
                        {data.source_of_funds_details && (
                          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{data.source_of_funds_details}</p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-center py-4" style={{ color: 'var(--text-tertiary)' }}>No personal data submitted yet</p>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                Uploaded Documents ({profileLoading ? '…' : allDocs.length})
              </p>
              <div className="space-y-2">
                {profileLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                ) : allDocs.length > 0 ? allDocs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 px-4 py-3 border rounded-lg transition-smooth"
                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                    <div className="p-2 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      {doc.mime_type?.startsWith('image/')
                        ? <ImageIcon className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                        : <FileText  className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                        {doc.document_type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {doc.file_name} • {(doc.file_size / 1024).toFixed(0)} KB • <span className="capitalize">{doc._level}</span> level
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={doc.review_status} />
                      <button onClick={() => setViewingDoc(doc)}
                        className="px-3 py-1.5 border rounded-lg text-xs inline-flex items-center gap-1.5 transition-smooth"
                        style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                        <Eye className="w-3.5 h-3.5" />View
                      </button>
                    </div>
                  </div>
                )) : (
                  <p className="text-xs text-center py-6" style={{ color: 'var(--text-tertiary)' }}>No documents uploaded yet</p>
                )}
              </div>
            </div>

            {/* Only show decision panel if this level is actually pending */}
            {application.pending_level && application[`${application.pending_level}_status`] === 'pending' && (
              <div className="border-t pt-6" style={{ borderColor: 'var(--border-color)' }}>
                <p className="text-xs font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>Your Decision</p>
                <div className="flex gap-2 mb-3">
                  <button onClick={() => setAction('approve')}
                    className="flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-smooth"
                    style={{ backgroundColor: action === 'approve' ? 'var(--accent-success)' : 'var(--bg-secondary)', color: action === 'approve' ? '#fff' : 'var(--text-primary)' }}>
                    <CheckCircle className="w-4 h-4 inline mr-1.5" />Approve
                  </button>
                  <button onClick={() => setAction('reject')}
                    className="flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-smooth"
                    style={{ backgroundColor: action === 'reject' ? 'var(--accent-danger)' : 'var(--bg-secondary)', color: action === 'reject' ? '#fff' : 'var(--text-primary)' }}>
                    <XCircle className="w-4 h-4 inline mr-1.5" />Reject
                  </button>
                </div>
                {action === 'reject' && (
                  <div>
                    <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                      Rejection Reason <span style={{ color: 'var(--accent-danger)' }}>*</span>
                    </label>
                    <textarea value={reason} onChange={e => setReason(e.target.value)}
                      placeholder="Explain why this application is being rejected (required)"
                      rows={3} className="w-full px-3 py-2 border rounded-lg text-xs focus:outline-none resize-none"
                      style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t flex items-center justify-between"
            style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-primary)' }}>
            <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm transition-smooth"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>Close</button>
            {application.pending_level && application[`${application.pending_level}_status`] === 'pending' && (
              <button onClick={handleSubmit} disabled={!action || submitting}
                className="px-6 py-2 text-white rounded-lg text-sm font-medium transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                {submitting ? 'Submitting…' : 'Submit Review'}
              </button>
            )}
          </div>
        </div>
      </div>
      {viewingDoc && <DocumentViewer document={viewingDoc} onClose={() => setViewingDoc(null)} />}
    </>
  );
}

function StatisticsPanel({ stats, isMobile }) {
  if (!stats) return null;
  const verified = stats.overview?.verified || {};
  const pending  = stats.overview?.pending  || {};
  const total    = stats.overview?.totalUsers || 0;

  const items = [
    { label: 'Total Users',       value: total,                  pendingCount: null,               color: 'var(--text-primary)',   cardBg: 'var(--bg-secondary)'  },
    { label: 'Basic Verified',    value: verified.basic    || 0, pendingCount: pending.basic    || 0, color: 'var(--accent-success)', cardBg: 'color-mix(in srgb, var(--accent-success) 8%, var(--bg-secondary))' },
    { label: 'Moderate Verified', value: verified.moderate || 0, pendingCount: pending.moderate || 0, color: 'var(--accent-info)',    cardBg: 'color-mix(in srgb, var(--accent-info)    8%, var(--bg-secondary))' },
    { label: 'Fully Verified',    value: verified.verified || 0, pendingCount: pending.verified || 0, color: 'var(--accent-primary)', cardBg: 'color-mix(in srgb, var(--accent-primary) 8%, var(--bg-secondary))' },
  ];

  if (isMobile) {
    return (
      <div className="grid grid-cols-2 gap-2 px-4">
        {items.map(({ label, value, pendingCount, color, cardBg }) => (
          <div key={label} className="rounded-2xl p-3 border" style={{ backgroundColor: cardBg, borderColor: 'var(--border-color)' }}>
            <p className="text-2xl font-black font-mono" style={{ color }}>{value}</p>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>{label}</p>
            {pendingCount > 0 && <p className="text-[10px] mt-0.5" style={{ color }}>{`+${pendingCount} pending`}</p>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {items.map(({ label, value, pendingCount, color, cardBg }) => (
        <div key={label} className="border rounded-lg p-4" style={{ backgroundColor: cardBg, borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</p>
          </div>
          <p className="text-2xl font-bold" style={{ color }}>{value}</p>
          {pendingCount > 0 && <p className="text-xs mt-1" style={{ color }}>{`+${pendingCount} pending`}</p>}
        </div>
      ))}
    </div>
  );
}


// ==================== ALL APPLICATIONS TAB ====================

function AllApplicationsTab({ onReview }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [selectedApp, setSelectedApp]   = useState(null);
  const [searchQuery, setSearchQuery]   = useState('');
  const [levelFilter, setLevelFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200 };
      if (levelFilter)  params.level  = levelFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await kycAPI.getAllApplications(params);
      setApplications(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [levelFilter, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = applications.filter(app => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      app.email?.toLowerCase().includes(q) ||
      app.first_name?.toLowerCase().includes(q) ||
      app.last_name?.toLowerCase().includes(q) ||
      String(app.user_id).includes(q)
    );
  });

  return (
    <>
      <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or ID..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">All Levels</option>
              <option value="basic">Basic</option>
              <option value="moderate">Moderate</option>
              <option value="verified">Verified</option>
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {filtered.length} user{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 border rounded-lg px-4 py-3 text-sm"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 25%, transparent)', color: 'var(--accent-danger)' }}>
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      <div className="border rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
              <tr>
                {['User', 'Current Level', 'Basic', 'Moderate', 'Verified', 'Docs', 'Last Updated', 'Actions'].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-medium ${i === 7 ? 'text-right' : 'text-left'}`}
                    style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent-primary)' }} />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center">
                  <Users className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--text-tertiary)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No applications found</p>
                </td></tr>
              ) : filtered.map(app => {
                const hasPending = app.basic_status === 'pending' || app.moderate_status === 'pending' || app.verified_status === 'pending';
                const pendingLevel = app.basic_status === 'pending' ? 'basic' : app.moderate_status === 'pending' ? 'moderate' : app.verified_status === 'pending' ? 'verified' : null;
                return (
                  <tr key={app.user_id} className="transition-smooth"
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {[app.first_name, app.last_name].filter(Boolean).join(' ') || 'Unknown'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{app.email} • #{app.user_id}</p>
                    </td>
                    <td className="px-4 py-3">
                      {app.current_level && app.current_level !== 'none'
                        ? <LevelBadge level={app.current_level} />
                        : <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>None</span>}
                    </td>
                    {['basic', 'moderate', 'verified'].map(l => (
                      <td key={l} className="px-4 py-3">
                        {app[`${l}_status`] && app[`${l}_status`] !== 'not_started' ? (
                          <div>
                            <StatusBadge status={app[`${l}_status`]} />
                            {app[`${l}_submitted_at`] && (
                              <p className="text-[10px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                                {new Date(app[`${l}_submitted_at`]).toLocaleDateString()}
                              </p>
                            )}
                            {app[`${l}_rejection_reason`] && (
                              <p className="text-[10px] mt-0.5 truncate max-w-[120px]" style={{ color: 'var(--accent-danger)' }}
                                title={app[`${l}_rejection_reason`]}>
                                {app[`${l}_rejection_reason`]}
                              </p>
                            )}
                          </div>
                        ) : <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{app.total_documents || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{new Date(app.updated_at).toLocaleDateString()}</p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{new Date(app.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelectedApp({ ...app, pending_level: pendingLevel })}
                        className="px-3 py-1.5 border rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-smooth"
                        style={{
                          backgroundColor: hasPending ? 'var(--accent-primary)' : 'transparent',
                          color: hasPending ? '#fff' : 'var(--text-primary)',
                          borderColor: hasPending ? 'var(--accent-primary)' : 'var(--border-color)',
                        }}>
                        <Eye className="w-3.5 h-3.5" />{hasPending ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedApp && (
        <ReviewModal application={selectedApp} onClose={() => { setSelectedApp(null); fetchAll(); }} onReview={onReview} />
      )}
    </>
  );
}

// ==================== AUDIT LOG TAB ====================

function AuditLogTab() {
  const [log, setLog]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [levelFilter, setLevelFilter]   = useState('');
  const [searchQuery, setSearchQuery]   = useState('');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200 };
      if (actionFilter) params.action = actionFilter;
      if (levelFilter)  params.level  = levelFilter;
      const res = await kycAPI.getAuditLog(params);
      setLog(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, levelFilter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const filtered = log.filter(entry => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      entry.email?.toLowerCase().includes(q) ||
      entry.first_name?.toLowerCase().includes(q) ||
      entry.last_name?.toLowerCase().includes(q) ||
      String(entry.user_id).includes(q)
    );
  });

  const actionOptions = [
    { value: '',                  label: 'All Actions'       },
    { value: 'submitted',         label: 'Submitted'         },
    { value: 'approved',          label: 'Approved'          },
    { value: 'rejected',          label: 'Rejected'          },
    { value: 'document_uploaded', label: 'Document Uploaded' },
    { value: 'document_approved', label: 'Document Approved' },
    { value: 'document_rejected', label: 'Document Rejected' },
    { value: 'level_upgraded',    label: 'Level Upgraded'    },
    { value: 'resubmission',      label: 'Resubmission'      },
  ];

  return (
    <div className="space-y-4">

      {/* Filters */}
      <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or ID..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              {actionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">All Levels</option>
              <option value="basic">Basic</option>
              <option value="moderate">Moderate</option>
              <option value="verified">Verified</option>
            </select>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 border rounded-lg px-4 py-3 text-sm"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 25%, transparent)', color: 'var(--accent-danger)' }}>
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
              <tr>
                {['User', 'Action', 'Level', 'Performed By', 'Details', 'Date & Time'].map((h, i) => (
                  <th key={h} className="px-4 py-3 text-xs font-medium text-left"
                    style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent-primary)' }} />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <ClipboardList className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--text-tertiary)' }} />
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No audit records found</p>
                  </td>
                </tr>
              ) : filtered.map((entry, idx) => {
                let details = null;
                if (entry.details) {
                  try { details = typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details; } catch {}
                }
                return (
                  <tr key={idx} className="transition-smooth"
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>

                    {/* User */}
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {[entry.first_name, entry.last_name].filter(Boolean).join(' ') || 'Unknown'}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {entry.email} • #{entry.user_id}
                      </p>
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3">
                      <AuditActionBadge action={entry.action} />
                    </td>

                    {/* Level */}
                    <td className="px-4 py-3">
                      {entry.kyc_level ? <LevelBadge level={entry.kyc_level} /> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>

                    {/* Performed By */}
                    <td className="px-4 py-3">
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                        {entry.performed_by_type === 'system' ? 'System (Auto)' :
                         entry.performed_by_type === 'user'   ? `Trader #${entry.first_name}`        :
                         entry.performed_by_type === 'admin'  ? 'Admin' :
                         entry.performed_by || '—'}
                      </p>
                      {entry.ip_address && (
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{entry.ip_address}</p>
                      )}
                    </td>

                    {/* Details */}
                    <td className="px-4 py-3 max-w-[220px]">
                      {details?.reason && (
                        <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}
                          title={details.reason}>
                          {details.reason}
                        </p>
                      )}
                      {details?.newLevel && (
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Upgraded to <span className="font-medium">{details.newLevel}</span>
                        </p>
                      )}
                      {!details?.reason && !details?.newLevel && (
                        <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                      )}
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3">
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                        {new Date(entry.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN DASHBOARD ====================

export default function BrokerKycDashboard() {
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab]       = useState('pending');
  const [pending, setPending]     = useState([]);
  const [stats, setStats]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [selectedApp, setSelectedApp]   = useState(null);
  const [levelFilter, setLevelFilter]   = useState('all');
  const [searchQuery, setSearchQuery]   = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pendingRes, statsRes] = await Promise.all([
        kycAPI.getPending({ limit: 100 }),
        kycAPI.getStatistics(),
      ]);
      setPending(Array.isArray(pendingRes.data?.data) ? pendingRes.data.data : []);
      setStats(statsRes.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load KYC data');
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReview = async (userId, level, action, reason) => {
    try {
      await kycAPI.reviewApplication(userId, level, { action, reason });
      await fetchData();
    } catch (e) {
      throw new Error(e.response?.data?.error || e.message || 'Review failed');
    }
  };

  const filteredPending = pending.filter(app => {
    if (levelFilter !== 'all' && app.pending_level !== levelFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        app.email?.toLowerCase().includes(q) ||
        app.first_name?.toLowerCase().includes(q) ||
        app.last_name?.toLowerCase().includes(q) ||
        String(app.user_id).includes(q)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  const tabs = [
    { key: 'pending',      label: 'Pending Review',   count: pending.length },
    { key: 'applications', label: 'All Applications',  count: null                },
    { key: 'audit',        label: 'Audit Log',         count: null                },
  ];

  // ── MOBILE ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="px-4 pt-5 pb-5" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>KYC Verification</p>
          <p className="text-4xl font-black text-white leading-none font-mono">{filteredPending.length}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
            pending application{filteredPending.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="py-4"><StatisticsPanel stats={stats} isMobile /></div>

        {/* Mobile Tabs */}
        <div className="flex border-b px-4" style={{ borderColor: 'var(--border-color)' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className="flex-1 py-2.5 text-xs font-bold relative"
              style={{ color: activeTab === t.key ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
              {t.label}
              {t.count !== null && t.count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px]"
                  style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}>{t.count}</span>
              )}
              {activeTab === t.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ backgroundColor: 'var(--accent-primary)' }} />
              )}
            </button>
          ))}
        </div>

        {activeTab === 'applications' && (
          <>
            <div className="px-4 pb-3 pt-3 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search name, email, ID…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border text-sm focus:outline-none"
                  style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {[['all','All'], ['basic','Basic'], ['moderate','Moderate'], ['verified','Verified']].map(([key, label]) => (
                  <button key={key} onClick={() => setLevelFilter(key)}
                    className="flex-shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-full"
                    style={{
                      backgroundColor: levelFilter === key ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                      color: levelFilter === key ? '#fff' : 'var(--text-secondary)',
                      border: levelFilter === key ? 'none' : '1px solid var(--border-color)',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="mx-4 mb-3 flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs border"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 25%, transparent)', color: 'var(--accent-danger)' }}>
                <AlertCircle className="w-4 h-4 shrink-0" />{error}
              </div>
            )}

            {filteredPending.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <CheckCircle className="w-10 h-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {searchQuery || levelFilter !== 'all' ? 'No matching applications' : 'No pending applications'}
                </p>
              </div>
            ) : (
              <div className="px-4 pb-8 space-y-2">
                {filteredPending.map(app => {
                  const level = app.pending_level;
                  const submittedAt = app[`${level}_submitted_at`];
                  return (
                    <div key={`${app.user_id}-${level}`} className="rounded-2xl border overflow-hidden"
                      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0"
                          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                          {app.first_name?.[0]}{app.last_name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>
                            {[app.first_name, app.last_name].filter(Boolean).join(' ') || 'Unknown'}
                          </p>
                          <p className="text-[11px] truncate" style={{ color: 'var(--text-tertiary)' }}>{app.email}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <LevelBadge level={level} />
                          <button onClick={() => setSelectedApp(app)}
                            className="text-[11px] font-bold px-3 py-1.5 rounded-xl text-white"
                            style={{ backgroundColor: 'var(--accent-primary)' }}>
                            Review
                          </button>
                        </div>
                      </div>
                      <div className="px-4 pb-3 flex items-center gap-2">
                        <StatusBadge status="pending" />
                        {submittedAt && (
                          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                            {new Date(submittedAt).toLocaleDateString()}
                          </span>
                        )}
                        <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>#{app.user_id}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'applications' && (
          <div className="px-4 pt-3 pb-8">
            <AllApplicationsTab onReview={handleReview} />
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="px-4 pt-3 pb-8">
            <AuditLogTab />
          </div>
        )}

        {selectedApp && (
          <ReviewModal application={selectedApp} onClose={() => setSelectedApp(null)} onReview={handleReview} />
        )}
        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>KYC Verification</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            Review and manage customer verification applications
          </p>
        </div>
        <a href="/plugins/kyc_verification" className="px-4 py-2 border rounded-lg text-sm inline-flex items-center gap-1.5 transition-smooth"
          style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
          <Shield className="w-4 h-4" />Plugin Settings
        </a>
      </div>

      {error && (
        <div className="flex items-center gap-2 border rounded-lg px-4 py-3 text-sm"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--accent-danger) 25%, transparent)', color: 'var(--accent-danger)' }}>
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      <StatisticsPanel stats={stats} isMobile={false} />

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: 'var(--border-color)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className="px-5 py-2.5 text-sm font-medium relative mr-1"
            style={{ color: activeTab === t.key ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
            {t.label}
            {t.count !== null && t.count > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs"
                style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}>{t.count}</span>
            )}
            {activeTab === t.key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full" style={{ backgroundColor: 'var(--accent-primary)' }} />
            )}
          </button>
        ))}
      </div>

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <>
          <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, or ID..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none"
                  style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm focus:outline-none"
                  style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="all">All Levels</option>
                  <option value="basic">Basic</option>
                  <option value="moderate">Moderate</option>
                  <option value="verified">Verified</option>
                </select>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {filteredPending.length} application{filteredPending.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                  <tr>
                    {['User', 'Level', 'Submitted', 'Status', 'Actions'].map((h, i) => (
                      <th key={h} className={`px-4 py-3 text-xs font-medium ${i === 4 ? 'text-right' : 'text-left'}`}
                        style={{ color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                  {filteredPending.map(app => {
                    const level = app.pending_level;
                    const submittedAt = app[`${level}_submitted_at`];
                    return (
                      <tr key={`${app.user_id}-${level}`} className="transition-smooth"
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {[app.first_name, app.last_name].filter(Boolean).join(' ') || 'Unknown'}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{app.email} • #{app.user_id}</p>
                        </td>
                        <td className="px-4 py-3"><LevelBadge level={level} /></td>
                        <td className="px-4 py-3">
                          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                            {submittedAt ? new Date(submittedAt).toLocaleDateString() : '—'}
                          </p>
                          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {submittedAt ? new Date(submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status="pending" /></td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setSelectedApp(app)}
                            className="px-3 py-1.5 text-white rounded-lg text-xs font-medium transition-smooth"
                            style={{ backgroundColor: 'var(--accent-primary)' }}>Review</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredPending.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-tertiary)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {searchQuery || levelFilter !== 'all' ? 'No matching applications' : 'No pending applications'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'applications' && <AllApplicationsTab onReview={handleReview} />}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && <AuditLogTab />}

      {selectedApp && (
        <ReviewModal application={selectedApp} onClose={() => setSelectedApp(null)} onReview={handleReview} />
      )}
    </div>
  );
}